import { withAlpha } from "@/lib/color";
import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LoanOptionDecisions from "@/components/LoanOptionDecisions";
import { formatMln, StipendBox } from "./bilancio/helpers";
import { StipendiVsPremiPanel, RulesPanel, CategoryPanel } from "./bilancio/panels";
import { AddMovementModal, FineModal, UglyPurchaseModal, StadiumPaymentModal, PayrollPreviewModal } from "./bilancio/modals";

// Feature flag: pulsante "Ritiro rate prestiti" nascosto finché non viene attivato.
// Backend attivo; mettere a true per mostrarlo.
const OP_LOANS_ENABLED = false;

export default function Bilancio() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [overview, setOverview] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [movements, setMovements] = useState([]);
    const [filter, setFilter] = useState("");
    const [err, setErr] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [showFine, setShowFine] = useState(false);
    const [showUgly, setShowUgly] = useState(false);
    const [showStadium, setShowStadium] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [chargePreview, setChargePreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [salaryRule, setSalaryRule] = useState("best_of_two");
    const [divDiscounts, setDivDiscounts] = useState(null);

    const load = async () => {
        try {
            const [a, b, c] = await Promise.all([
                api.get("/treasury"),
                api.get("/treasury/movements", { params: { limit: 500 } }),
                api.get("/treasury/forecast"),
            ]);
            setOverview(a.data);
            setMovements(b.data || []);
            setForecast(c.data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        }
    };

    /* eslint-disable */
    useEffect(() => { load(); }, []);
    useEffect(() => {
        api.get("/admin/settings/salary-rule")
            .then((r) => setSalaryRule(r.data?.salary_rule || "best_of_two"))
            .catch(() => {});
        api.get("/settings/divisions")
            .then((r) => setDivDiscounts(r.data || {}))
            .catch(() => {});
    }, []);
    /* eslint-enable */

    const changeSalaryRule = async (rule) => {
        if (rule === salaryRule || busy) return;
        const labels = { best_of_two: "Best of Two (più favorevole)", always_new: "Sempre il Nuovo" };
        if (!window.confirm(`Cambiare la regola stipendi in "${labels[rule]}"?\n\nAvrà effetto immediato su TUTTA la lega (rose, monte ingaggi, rinnovi).`)) return;
        setBusy(true);
        try {
            await api.put("/admin/settings/salary-rule", { salary_rule: rule });
            setSalaryRule(rule);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const visibleMovements = useMemo(() => {
        return filter ? movements.filter((m) => m.kind === filter) : movements;
    }, [movements, filter]);

    // Helper: chiede l'anteprima all'endpoint (preview=true), apre il modale di
    // conferma e, alla conferma, riesegue la stessa chiamata commitante (preview=false).
    const runChargeWithPreview = async ({ title, endpoint, body }) => {
        setBusy(true); setErr("");
        try {
            const { data } = await api.post(endpoint, { ...body, preview: true });
            setChargePreview({
                title,
                items: data.collected || [],
                skipped: data.skipped || [],
                total: data.total || (data.collected || []).reduce((s, c) => s + (c.amount || 0), 0),
                count: data.count ?? (data.collected || []).length,
                onConfirm: async () => {
                    setBusy(true); setErr("");
                    try {
                        await api.post(endpoint, { ...body, preview: false });
                        setChargePreview(null);
                        await load();
                    } catch (e) {
                        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                    } finally { setBusy(false); }
                },
            });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const collectStipends = async (rateNum, component) => {
        const compLabels = {
            base: "stipendi base",
            vg11: "VG11",
            all: "stipendi base + VG11",
        };
        const seasonLabel = window.prompt(`Etichetta stagione per Rata ${rateNum} ${compLabels[component]} (es. 'Stagione 2025/26'):`, "Stagione 2025/26");
        if (!seasonLabel) return;
        const fullLabel = `Rata ${rateNum} ${compLabels[component]} — ${seasonLabel}`;
        await runChargeWithPreview({
            title: `Anteprima — ${fullLabel}`,
            endpoint: "/treasury/collect-stipends",
            body: { rate_label: fullLabel, component },
        });
    };

    const collectAnnualTax = async () => {
        const label = window.prompt("Etichetta (es. 'Tasse 2025/26'):", "Tasse 2025/26");
        if (!label) return;
        await runChargeWithPreview({
            title: `Anteprima — Tasse 10% (${label})`,
            endpoint: "/treasury/collect-annual-tax",
            body: { season_label: label, rate: 0.10 },
        });
    };

    const collectStadiumMaintenance = async () => {
        const label = window.prompt("Etichetta (es. 'Manutenzione 2025/26'):", "Manutenzione 2025/26");
        if (!label) return;
        await runChargeWithPreview({
            title: `Anteprima — Manutenzione Stadi (${label})`,
            endpoint: "/treasury/collect-stadium-maintenance",
            body: { season_label: label },
        });
    };

    const syncSeasonTags = async () => {
        const label = window.prompt(
            "SINCRONIZZA prelievi già fatti con la stagione corrente.\n\nUsa questo SOLO per i prelievi eseguiti prima dell'aggiornamento: assegna il tag stagione così il 'Saldo fine stagione' li riconosce come già pagati.\n\nInserisci una parte dell'etichetta stagione usata nei prelievi (es. '2025/26'):",
            "2025/26"
        );
        if (!label) return;
        setErr("");
        try {
            setBusy(true);
            const { data } = await api.post("/admin/treasury/backfill-season-num", { season_label: label, preview: true });
            if (!data.count) {
                window.alert(`Nessun prelievo da sincronizzare per «${label}».\n\nMovimenti senza tag non corrispondenti: ${data.unmatched_untagged}.\nControlla l'etichetta (prova solo l'anno, es. '2025/26') e riprova.`);
                return;
            }
            const lines = data.items.slice(0, 40)
                .map((it) => `• ${it.team_name} — ${it.kind} — ${(it.amount / 1e6).toFixed(2)}mln [${it.label}]`)
                .join("\n");
            const more = data.count > 40 ? `\n…e altri ${data.count - 40}` : "";
            const warn = data.unmatched_untagged > 0
                ? `\n\n⚠ Attenzione: ${data.unmatched_untagged} movimenti senza tag NON corrispondono a «${label}» e resteranno esclusi.`
                : "";
            const ok = window.confirm(
                `ANTEPRIMA — ${data.count} movimenti verranno assegnati alla stagione ${data.season_num}:\n\n${lines}${more}${warn}\n\nApplicare?`
            );
            if (!ok) return;
            const res = await api.post("/admin/treasury/backfill-season-num", { season_label: label, preview: false });
            window.alert(`Fatto! ${res.data.updated} movimenti taggati con la stagione ${res.data.season_num}.\n\nRiapri la scheda finanziaria di una squadra: le rate già prelevate appariranno barrate e il saldo non cambierà più.`);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const collectLoanInstallments = async () => {
        await runChargeWithPreview({
            title: "Anteprima — Ritiro rate prestiti Banca OP",
            endpoint: "/admin/op-loans/collect",
            body: {},
        });
    };

    const runSeasonWizard = async (phase) => {
        const phases = {
            start: { label: "INIZIO STAGIONE", endpoint: "/treasury/season-start-wizard" },
            mid: { label: "METÀ STAGIONE (fine mercato invernale)", endpoint: "/treasury/mid-season-wizard" },
            end: { label: "FINE STAGIONE", endpoint: "/treasury/season-end-wizard" },
        };
        const p = phases[phase];
        const label = window.prompt("Etichetta stagione (es. 'Stagione 2025/26'):", "Stagione 2025/26");
        if (!label) return;
        setBusy(true); setErr("");
        try {
            const { data } = await api.post(p.endpoint, { season_label: label, preview: true });
            setChargePreview({
                mode: "wizard",
                title: `Wizard ${p.label} — ${label}`,
                items: data.items || [],
                skipped: data.skipped || [],
                count: data.count || 0,
                tvTotal: data.tv_total || 0,
                debitTotal: data.debit_total || 0,
                opBefore: data.op_balance_before || 0,
                opAfter: data.op_balance_after || 0,
                onConfirm: async () => {
                    setBusy(true); setErr("");
                    try {
                        await api.post(p.endpoint, { season_label: label, preview: false });
                        setChargePreview(null);
                        await load();
                    } catch (e) {
                        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                    } finally { setBusy(false); }
                },
            });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const runContractRollover = async (dryRun) => {
        if (!dryRun && !window.confirm(
            "PASSAGGIO DI STAGIONE — CONTRATTI\n\n" +
            "Scala di 1 anno il contratto di TUTTI i giocatori in rosa.\n" +
            "Chi arriva a 0 (non rinnovato) viene AUTO-RINNOVATO a 1 anno allo STIPENDIO NUOVO " +
            "(condizioni peggiori, perde lo sconto Best-of-Two).\n\n" +
            "⚠️ Operazione irreversibile su tutta la lega. Consigliato un backup prima.\n\n" +
            "Procedo?"
        )) return;
        setBusy(true);
        try {
            const r = await api.post("/contracts/season-rollover", { dry_run: dryRun });
            const d = r.data;
            if (dryRun) {
                window.alert(
                    "ANTEPRIMA Passaggio di Stagione (nessuna modifica salvata)\n\n" +
                    `Contratti totali: ${d.total_processed}\n` +
                    `• Scalati di 1 anno: ${d.aged}\n` +
                    `• Auto-rinnovati a 1 anno (stipendio nuovo): ${d.auto_renewed}`
                );
            } else {
                await load();
                window.alert(
                    "Passaggio di Stagione completato!\n\n" +
                    `Contratti aggiornati: ${d.total_processed}\n` +
                    `• Scalati di 1 anno: ${d.aged}\n` +
                    `• Auto-rinnovati a 1 anno: ${d.auto_renewed}`
                );
            }
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const runEndSeasonLoans = async (dryRun) => {
        if (!dryRun && !window.confirm(
            "FINE STAGIONE — PRESTITI\n\n" +
            "• Prestito secco → il giocatore TORNA alla squadra d'origine\n" +
            "• Obbligo di riscatto → riscatto AUTOMATICO (il club paga, giocatore definitivo)\n" +
            "• Diritto di riscatto → crea una decisione per il presidente (Esercita / Rifiuta) + notifica\n\n" +
            "⚠️ Operazione che muove denaro e rose. Consigliato un backup prima.\n\nProcedo?"
        )) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/loans/end-season", { dry_run: dryRun });
            const d = r.data;
            const head = dryRun ? "ANTEPRIMA Fine Stagione Prestiti (nessuna modifica)" : "Fine Stagione Prestiti completata!";
            if (!dryRun) await load();
            window.alert(
                `${head}\n\n` +
                `• Prestiti secchi rientrati: ${d.returned_count}\n` +
                `• Obblighi di riscatto eseguiti: ${d.bought_count}\n` +
                `• Diritti di riscatto in attesa di decisione: ${d.options_count}`
            );
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };


    const distributeTVRights = async () => {
        const label = window.prompt("Etichetta stagione (es. 'Stagione 2025/26'):", "Stagione 2025/26");
        if (!label) return;
        setBusy(true);
        try {
            // 1) Anteprima (non accredita)
            const prev = await api.post("/treasury/distribute-tv-rights", { season_label: label, preview: true });
            const d = prev.data;
            if (!d.count) {
                window.alert(d.skipped?.length
                    ? `Nessuna squadra da accreditare: tutte già accreditate per "${label}".`
                    : "Nessuna squadra da accreditare.");
                setBusy(false);
                return;
            }
            const byDiv = {};
            (d.distributed || []).forEach((x) => { const k = x.division || "—"; byDiv[k] = (byDiv[k] || 0) + 1; });
            const divLines = Object.entries(byDiv).map(([k, v]) => `   ${k}: ${v} squadre`).join("\n");
            const skippedLine = d.skipped?.length ? `\n⚠️ ${d.skipped.length} squadre già accreditate (saltate).` : "";
            const ok = window.confirm(
                `ANTEPRIMA — Accredita Diritti TV "${label}"\n\n` +
                `Squadre da accreditare: ${d.count}\n` +
                `Totale da distribuire: ${(d.total / 1e6).toFixed(0)} mln\n\n` +
                `Per divisione:\n${divLines}${skippedLine}\n\n` +
                `BASE 100mln + campionato per divisione (TOP 50 / DOP 20 / OPS 10).\n` +
                `Scalati da Cassa OP, accreditati al budget squadre.\n\nConfermi l'accredito?`
            );
            if (!ok) { setBusy(false); return; }
            // 2) Accredito reale
            const r = await api.post("/treasury/distribute-tv-rights", { season_label: label });
            await load();
            window.alert(`Distribuiti ${(r.data.total / 1e6).toFixed(0)} mln a ${r.data.count} squadre.\nOra spariranno dalle previsioni delle schede finanziarie.`);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const removeMovement = async (createdAt) => {
        if (!window.confirm("Eliminare questo movimento? Il saldo verrà ricalcolato.")) return;
        try {
            await api.delete(`/treasury/movements/${encodeURIComponent(createdAt)}`);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    if (!overview) return <div className="p-8 op-label text-t8f95a3 animate-pulse">// CARICAMENTO BILANCIO…</div>;

    const { balance, summary, kinds } = overview;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="bilancio-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="op-heading text-4xl sm:text-5xl">BILANCIO <span className="op-gradient-text">OP</span></h1>
                    <p className="op-mono text-xs text-t8f95a3 mt-2 max-w-xl">
                        Ogni euro speso da OP deve esistere in cassa. Nessuna creazione di denaro dal nulla.
                    </p>
                </div>
                <button
                    onClick={() => setShowRules((v) => !v)}
                    className="op-mono text-[11px] px-3 py-1.5 border border-t22252a text-t8f95a3 hover:text-tffffff hover:border-tffffff transition-colors"
                    data-testid="toggle-rules"
                >
                    {showRules ? "− Nascondi regole" : "+ Come funziona"}
                </button>
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-6 flex items-center justify-between" data-testid="bilancio-error">
                    <span>{err}</span>
                    <button onClick={() => setErr("")} className="op-mono text-[10px] ml-3">✕</button>
                </div>
            )}

            {showRules && <RulesPanel divDiscounts={divDiscounts} />}

            {/* Diritto di riscatto — decisioni da prendere (presidente: la sua squadra; admin: tutte) */}
            <LoanOptionDecisions onResolved={load} />

            {/* Balance hero */}
            <div className="op-card p-6 sm:p-8 mb-6 text-center">
                <div className="op-label text-t2bb6ff">// SALDO ATTUALE CASSA OP</div>
                <div className="op-heading text-5xl sm:text-7xl text-t2bb6ff mt-2" data-testid="balance">
                    {formatMln(balance)}
                </div>
                <div className="op-mono text-[10px] text-t5c6370 mt-3 uppercase">
                    Aggiornato {overview.updated_at?.slice(0, 16).replace("T", " ") || "—"}
                </div>
            </div>

            {/* Summary by category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <CategoryPanel
                    title="ENTRATE"
                    accent="rgb(var(--tc-2bb6ff))"
                    total={summary.total_in}
                    items={Object.entries(summary.inflows)}
                    labels={summary.labels}
                    forecast={forecast?.inflows}
                    forecastTotal={forecast?.total_in}
                    forecastLabels={forecast?.labels}
                    hideKeys={["stipend_collection", "vg11_collection"]}
                    hideHint="→ Stipendi base + VG11 nel dettaglio sotto"
                />
                <CategoryPanel
                    title="USCITE"
                    accent="rgb(var(--tc-ff3b30))"
                    total={summary.total_out}
                    items={[
                        ...Object.entries(summary.outflows).filter(([k]) => k !== "prize"),
                        ...Object.entries(summary.prizes_by_category || {}),
                    ]}
                    labels={summary.labels}
                    forecast={forecast?.outflows}
                    forecastTotal={forecast?.total_out}
                    forecastLabels={forecast?.labels}
                    hideKeys={["prize"]}
                    hideHint="Premi mostrati per categoria: REALE (già distribuito) con accanto il PREVISTO da regolamento."
                />
            </div>

            {/* Forecast summary */}
            {forecast && (
                <div className="op-card p-5 mb-6 border-l-2 border-l-te8ff00" data-testid="forecast-summary">
                    <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
                        <div>
                            <div className="op-label text-te8ff00">// PROIEZIONE STAGIONE</div>
                            <p className="op-mono text-[11px] text-t8f95a3 mt-1">
                                Solo voci certe: tasse, stipendi, premi NL, diritti TV NL, Fair Play, premi individuali.
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="op-mono text-[10px] text-t8f95a3">Saldo previsto fine stagione</div>
                            <div className={`op-heading text-2xl ${forecast.net + (overview.balance || 0) < 0 ? "text-tff3b30" : "text-t2bb6ff"}`} data-testid="forecast-projected-balance">
                                {formatMln((overview.balance || 0) + forecast.net)}
                            </div>
                            <div className="op-mono text-[10px] text-t8f95a3">
                                (oggi {formatMln(overview.balance)} + netto previsto {forecast.net >= 0 ? "+" : "−"}{formatMln(Math.abs(forecast.net))})
                            </div>
                        </div>
                    </div>

                    {/* Stipends breakdown — explicit VG11 + division impact */}
                    {forecast.stipends_breakdown && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3" data-testid="stipends-breakdown">
                            <StipendBox label="Stipendi base" value={forecast.stipends_breakdown.base} color="rgb(var(--tc-c9ccd3))" />
                            <StipendBox
                                label="Contributo VG11"
                                value={forecast.stipends_breakdown.vg11_delta}
                                color={forecast.stipends_breakdown.vg11_delta >= 0 ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))"}
                                hint="(VG11 sopra 80 → squadre pagano di più)"
                                signed
                            />
                            <StipendBox
                                label="Sgravi divisione"
                                value={forecast.stipends_breakdown.division_savings}
                                color="rgb(var(--tc-ff6b00))"
                                hint={divDiscounts
                                    ? `(OPS −${Math.round((divDiscounts.OPS ?? 0) * 100)}%, HOPE −${Math.round((divDiscounts.HOPE ?? 0) * 100)}%)`
                                    : "(OPS −20%, HOPE −40%)"}
                                signed
                            />
                            <StipendBox label="Totale stipendi (entrata)" value={forecast.stipends_breakdown.final} color="rgb(var(--tc-2bb6ff))" bold />
                        </div>
                    )}

                    <details className="op-mono text-[11px] text-t8f95a3 cursor-pointer">
                        <summary className="hover:text-tffffff">+ Dettaglio note di calcolo</summary>
                        <ul className="mt-2 list-disc list-inside space-y-1">
                            {forecast.season_notes.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                    </details>
                </div>
            )}

            {/* Stipendi vs Premi — il quadro chiave */}
            {forecast?.comparison && (
                <StipendiVsPremiPanel comparison={forecast.comparison} actualPrizes={summary.prizes_by_category} />
            )}

            {/* Admin actions */}
            {isAdmin && (
                <div className="op-card p-5 mb-6" data-testid="admin-actions">
                    <div className="op-label text-te8ff00 mb-3">// AZIONI ADMIN</div>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Regola stipendi</div>
                    <div className="flex flex-wrap gap-2 mb-1" data-testid="salary-rule-toggle">
                        <button
                            onClick={() => changeSalaryRule("best_of_two")}
                            disabled={busy}
                            className="op-btn text-xs disabled:opacity-50"
                            style={salaryRule === "best_of_two" ? { background: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-000000))", fontWeight: 700 } : {}}
                            data-testid="salary-rule-best"
                        >
                            Best of Two (più favorevole)
                        </button>
                        <button
                            onClick={() => changeSalaryRule("always_new")}
                            disabled={busy}
                            className="op-btn text-xs disabled:opacity-50"
                            style={salaryRule === "always_new" ? { background: "rgb(var(--tc-ff6b00))", color: "rgb(var(--tc-000000))", fontWeight: 700 } : {}}
                            data-testid="salary-rule-new"
                        >
                            Sempre il Nuovo
                        </button>
                    </div>
                    <p className="op-mono text-[10px] text-t5c6370 mb-4 leading-snug" data-testid="salary-rule-active">
                        Attiva:{" "}
                        <b style={{ color: "rgb(var(--tc-e8ff00))" }}>
                            {salaryRule === "best_of_two"
                                ? "Best of Two — paga il MINORE tra stipendio vecchio e nuovo"
                                : "Sempre il Nuovo — paga sempre lo stipendio della formula nuova"}
                        </b>
                    </p>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Wizard di stagione</div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button onClick={() => runSeasonWizard("start")} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="wizard-start" style={{ background: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-000000))", fontWeight: 700 }}>
                            ⚡ INIZIO stagione
                        </button>
                        <button onClick={() => runSeasonWizard("mid")} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="wizard-mid" style={{ background: "rgb(var(--tc-e8ff00))", color: "rgb(var(--tc-000000))", fontWeight: 700 }}>
                            ⚡ METÀ stagione
                        </button>
                        <button onClick={() => runSeasonWizard("end")} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="wizard-end" style={{ background: "rgb(var(--tc-ff6b00))", color: "rgb(var(--tc-000000))", fontWeight: 700 }}>
                            ⚡ FINE stagione
                        </button>
                    </div>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Contratti — Passaggio di stagione</div>
                    <div className="flex flex-wrap gap-2 mb-2">
                        <button onClick={() => runContractRollover(true)} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="contract-rollover-preview">
                            👁 Anteprima passaggio
                        </button>
                        <button onClick={() => runContractRollover(false)} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="contract-rollover-run" style={{ background: "rgb(var(--tc-ff4d4d))", color: "rgb(var(--tc-ffffff))", fontWeight: 700 }}>
                            ↺ Esegui passaggio di stagione (contratti -1 anno)
                        </button>
                    </div>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Prestiti — Fine stagione & riscatti</div>
                    <div className="flex flex-wrap gap-2 mb-2">
                        <button onClick={() => runEndSeasonLoans(true)} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="end-season-loans-preview">
                            👁 Anteprima prestiti
                        </button>
                        <button onClick={() => runEndSeasonLoans(false)} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="end-season-loans-run" style={{ background: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-000000))", fontWeight: 700 }}>
                            ⚖️ Elabora fine stagione prestiti
                        </button>
                    </div>
                    <p className="op-mono text-[10px] text-t5c6370 mb-4 leading-snug">
                        Scala tutti i contratti di 1 anno. Chi arriva a 0 e non è stato rinnovato dal presidente
                        viene auto-rinnovato a 1 anno allo stipendio nuovo (condizioni peggiori). Fai prima un&apos;anteprima.
                    </p>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Singole operazioni — Inizio stagione</div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button onClick={distributeTVRights} disabled={busy} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="distribute-tv-rights" style={{ background: "rgb(var(--tc-e8ff00))", color: "rgb(var(--tc-000000))" }}>
                            1. Accredita Diritti TV <span className="op-mono text-[9px] opacity-70">(pre-mercato)</span>
                        </button>
                        <button onClick={() => collectStipends("1", "base")} disabled={busy} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="collect-rate-1-base">
                            2. Preleva Rata 1 Stipendi base <span className="op-mono text-[9px] opacity-70">(post-mercato)</span>
                        </button>
                        <button onClick={() => collectStipends("1", "vg11")} disabled={busy} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="collect-rate-1-vg11">
                            3. Preleva Rata 1 VG11
                        </button>
                    </div>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Singole operazioni — Metà stagione (fine mercato invernale)</div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button onClick={() => collectStipends("2", "base")} disabled={busy} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="collect-rate-2-base">
                            1. Preleva Rata 2 Stipendi base
                        </button>
                        <button onClick={() => collectStipends("2", "vg11")} disabled={busy} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="collect-rate-2-vg11">
                            2. Preleva Rata 2 VG11
                        </button>
                    </div>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Singole operazioni — Fine stagione</div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button onClick={collectAnnualTax} disabled={busy} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="collect-annual-tax">
                            1. Preleva Tasse 10%
                        </button>
                        <button onClick={collectStadiumMaintenance} disabled={busy} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="collect-stadium-maintenance">
                            2. Preleva Manutenzione Stadi
                        </button>
                        <span className="op-mono text-[10px] text-t8f95a3 self-center italic">
                            3. Premi piazzamento + coppe + Fair Play + individuali → bottone automatico (in arrivo) basato sui risultati finali.
                        </span>
                    </div>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Manutenzione dati</div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button onClick={syncSeasonTags} disabled={busy} className="op-btn op-btn-ghost text-xs disabled:opacity-50" data-testid="sync-season-tags" title="Assegna il tag stagione ai prelievi già fatti prima dell'aggiornamento (con anteprima)">
                            🔧 Sincronizza prelievi con stagione (retroattivo)
                        </button>
                        <span className="op-mono text-[10px] text-t5c6370 self-center italic max-w-lg leading-snug">
                            Solo per i prelievi eseguiti PRIMA dell&apos;aggiornamento: li marca con la stagione così la scheda finanziaria mostra le rate barrate e il saldo resta invariato. Mostra sempre un&apos;anteprima.
                        </span>
                    </div>

                    <div className="op-mono text-[10px] text-t8f95a3 uppercase mb-2">Operazioni puntuali (in qualsiasi momento)</div>
                    <div className="flex flex-wrap gap-2">
                        {OP_LOANS_ENABLED && (
                            <button onClick={collectLoanInstallments} disabled={busy} className="op-btn text-xs disabled:opacity-50" data-testid="collect-loan-installments" style={{ background: "rgb(var(--tc-e8ff00))", color: "rgb(var(--tc-000000))", fontWeight: 700 }}>
                                🏦 Ritiro rate prestiti
                            </button>
                        )}
                        <button onClick={() => setShowPreview(true)} className="op-btn op-btn-ghost text-xs" data-testid="preview-payroll">
                            Anteprima rata
                        </button>
                        <button onClick={() => setShowUgly(true)} className="op-btn op-btn-ghost text-xs" data-testid="add-ugly-purchase">
                            + Acquisto Ugly
                        </button>
                        <button onClick={() => setShowStadium(true)} className="op-btn op-btn-ghost text-xs" data-testid="add-stadium-payment">
                            + Pagamento stadio
                        </button>
                        <button onClick={() => setShowAdd(true)} className="op-btn op-btn-ghost text-xs" data-testid="add-movement-btn">
                            + Movimento custom
                        </button>
                        <button onClick={() => setShowFine(true)} className="op-btn op-btn-ghost text-xs text-tff4d4d border-tff4d4d/40 hover:bg-tff4d4d/10" data-testid="add-fine-btn">
                            💸 Genera multa
                        </button>
                    </div>
                </div>
            )}

            {/* Filter toolbar */}
            <div className="flex gap-2 mb-4 flex-wrap" data-testid="filter-toolbar">
                <button onClick={() => setFilter("")} className={`op-mono text-[11px] px-3 py-1.5 border ${!filter ? "bg-tffffff text-black border-tffffff" : "border-t22252a text-t8f95a3 hover:text-tffffff"}`}>
                    TUTTI
                </button>
                {kinds.map((k) => (
                    <button key={k.key} onClick={() => setFilter(k.key)} className="op-mono text-[11px] px-3 py-1.5 border transition-colors" style={{ background: filter === k.key ? (k.direction === "in" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))") : "transparent", color: filter === k.key ? "rgb(var(--tc-000000))" : (k.direction === "in" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))"), borderColor: withAlpha(k.direction === "in" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))", "55") }} data-testid={`filter-${k.key}`}>
                        {k.label}
                    </button>
                ))}
            </div>

            {/* Movements ledger */}
            <div className="op-card overflow-x-auto" data-testid="movements-ledger">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-t8f95a3 border-b border-t22252a">
                            <th className="text-left py-2 px-3 op-label text-[10px]">Data</th>
                            <th className="text-left py-2 px-3 op-label text-[10px]">Tipo</th>
                            <th className="text-left py-2 px-3 op-label text-[10px]">Descrizione</th>
                            <th className="text-right py-2 px-3 op-label text-[10px]">Importo</th>
                            <th className="text-right py-2 px-3 op-label text-[10px]">Saldo dopo</th>
                            {isAdmin && <th className="text-center py-2 px-3 op-label text-[10px]">Azioni</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleMovements.length === 0 && (
                            <tr><td colSpan={isAdmin ? 6 : 5} className="py-6 text-center op-mono text-t8f95a3 italic">Nessun movimento.</td></tr>
                        )}
                        {visibleMovements.map((m) => (
                            <tr key={m.created_at} className="border-b border-t1a1d24 hover:bg-t1a1d24/40">
                                <td className="py-2 px-3 op-mono text-[10px] text-t8f95a3 whitespace-nowrap">{m.created_at?.slice(0, 16).replace("T", " ")}</td>
                                <td className="py-2 px-3">
                                    <span className="op-mono text-[10px] uppercase px-2 py-0.5 border" style={{ color: m.direction === "in" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))", borderColor: withAlpha(m.direction === "in" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))", "66") }}>
                                        {(kinds.find((k) => k.key === m.kind)?.label) || m.kind}
                                    </span>
                                </td>
                                <td className="py-2 px-3 text-tc9ccd3">
                                    {m.description}
                                    {m.team_name && <span className="op-mono text-[10px] text-t8f95a3 ml-2">[{m.team_name}]</span>}
                                </td>
                                <td className="py-2 px-3 text-right op-mono font-bold" style={{ color: m.direction === "in" ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-ff3b30))" }}>
                                    {m.direction === "in" ? "+" : "−"}{formatMln(m.amount)}
                                </td>
                                <td className="py-2 px-3 text-right op-mono text-tffffff">{formatMln(m.balance_after)}</td>
                                {isAdmin && (
                                    <td className="py-2 px-3 text-center">
                                        <button onClick={() => removeMovement(m.created_at)} className="op-mono text-[10px] text-tff3b30 hover:underline" data-testid={`delete-${m.created_at}`}>✕</button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showAdd && <AddMovementModal kinds={kinds} onClose={() => setShowAdd(false)} onCreated={async () => { setShowAdd(false); await load(); }} />}
            {showFine && <FineModal onClose={() => setShowFine(false)} onCreated={async () => { setShowFine(false); await load(); }} />}
            {showUgly && <UglyPurchaseModal onClose={() => setShowUgly(false)} onCreated={async () => { setShowUgly(false); await load(); }} />}
            {showStadium && <StadiumPaymentModal onClose={() => setShowStadium(false)} onCreated={async () => { setShowStadium(false); await load(); }} />}
            {showPreview && <PayrollPreviewModal onClose={() => setShowPreview(false)} />}
            {chargePreview && (
                <ChargePreviewModal
                    data={chargePreview}
                    busy={busy}
                    error={err}
                    onClose={() => { setErr(""); setChargePreview(null); }}
                />
            )}
        </div>
    );
}

function ChargePreviewModal({ data, busy, error, onClose }) {
    const { title, items = [], skipped = [], total = 0, count = 0, onConfirm, mode } = data;
    const isWizard = mode === "wizard";
    const hasTv = isWizard && items.some((i) => i.tv > 0);
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="charge-preview-modal" onClick={onClose}>
            <div className="op-card w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-t22252a">
                    <div className="op-label text-t2bb6ff">// {isWizard ? "ANTEPRIMA WIZARD" : "ANTEPRIMA PRELIEVO"}</div>
                    <h2 className="op-heading text-xl mt-1 text-tffffff">{title}</h2>
                    {isWizard ? (
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 op-mono text-xs">
                            <span className="text-tc9ccd3">Squadre coinvolte: <b className="text-tffffff">{count}</b></span>
                            {data.tvTotal > 0 && <span className="text-tc9ccd3">Diritti TV distribuiti: <b className="text-t2bb6ff">{formatMln(data.tvTotal)}</b></span>}
                            {data.debitTotal > 0 && <span className="text-tc9ccd3">Prelievi incassati: <b className="text-t7ce38b">{formatMln(data.debitTotal)}</b></span>}
                            <span className="text-tc9ccd3">Cassa OP: <b className={data.opBefore < 0 ? "text-tff3b30" : "text-tffffff"}>{formatMln(data.opBefore)}</b> → <b className={data.opAfter < 0 ? "text-tff3b30" : "text-t2bb6ff"}>{formatMln(data.opAfter)}</b></span>
                            {skipped.length > 0 && <span className="text-t8f95a3">Saltate (già fatte): <b className="text-tffffff">{skipped.length}</b></span>}
                        </div>
                    ) : (
                        <div className="flex gap-6 mt-3 op-mono text-xs">
                            <span className="text-tc9ccd3">Squadre da addebitare: <b className="text-tffffff">{count}</b></span>
                            <span className="text-tc9ccd3">Totale in cassa OP: <b className="text-t2bb6ff">{formatMln(total)}</b></span>
                            {skipped.length > 0 && <span className="text-t8f95a3">Saltate (già pagate): <b className="text-tffffff">{skipped.length}</b></span>}
                        </div>
                    )}
                </div>
                <div className="overflow-y-auto flex-1 p-5">
                    {count === 0 ? (
                        <div className="op-mono text-sm text-t8f95a3 italic text-center py-6">
                            {isWizard ? "Niente da fare: tutte le squadre sono già state elaborate con questa etichetta." : "Nessuna voce da incassare al momento."}
                        </div>
                    ) : isWizard ? (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-t8f95a3 border-b border-t22252a">
                                    <th className="text-left py-2 op-label text-[10px]">Squadra</th>
                                    {hasTv && <th className="text-right py-2 op-label text-[10px]">Diritti TV</th>}
                                    <th className="text-right py-2 op-label text-[10px]">Prelievo</th>
                                    <th className="text-right py-2 op-label text-[10px]">Saldo dopo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => (
                                    <tr key={c.team} className="border-b border-t1a1d24">
                                        <td className="py-1.5 text-tc9ccd3">{c.team}</td>
                                        {hasTv && <td className="py-1.5 text-right op-mono text-t2bb6ff">{c.tv > 0 ? `+${formatMln(c.tv)}` : "—"}</td>}
                                        <td className="py-1.5 text-right op-mono text-tff3b30">{c.debit > 0 ? `−${formatMln(c.debit)}` : "—"}</td>
                                        <td className={`py-1.5 text-right op-mono ${c.net_budget < 0 ? "text-tff3b30" : "text-tffffff"}`}>{formatMln(c.net_budget)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-t8f95a3 border-b border-t22252a">
                                    <th className="text-left py-2 op-label text-[10px]">Squadra</th>
                                    <th className="text-right py-2 op-label text-[10px]">Addebito</th>
                                    <th className="text-right py-2 op-label text-[10px]">Saldo dopo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => (
                                    <tr key={c.team} className="border-b border-t1a1d24">
                                        <td className="py-1.5 text-tc9ccd3">
                                            {c.team}
                                            {c.levels?.length > 0 && <span className="op-mono text-[10px] text-t8f95a3 ml-2">{c.levels.join(", ")}</span>}
                                        </td>
                                        <td className="py-1.5 text-right op-mono font-bold text-tff3b30">−{formatMln(c.amount)}</td>
                                        <td className={`py-1.5 text-right op-mono ${c.new_budget < 0 ? "text-tff3b30" : "text-tffffff"}`}>{formatMln(c.new_budget)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {skipped.length > 0 && (
                        <div className="mt-4 op-mono text-[11px] text-t8f95a3">
                            <div className="uppercase mb-1">Saltate (già elaborate con questa etichetta):</div>
                            {skipped.map((s) => <div key={s.team}>• {s.team} — {formatMln(s.amount)}</div>)}
                        </div>
                    )}
                </div>
                <div className="p-5 border-t border-t22252a flex flex-col gap-3">
                    {error && (
                        <div className="op-mono text-xs text-tff3b30 bg-tff3b30/10 border border-tff3b30/40 rounded px-3 py-2" data-testid="charge-preview-error">
                            ⚠️ {error}
                        </div>
                    )}
                    <div className="flex justify-end gap-3">
                        <button onClick={onClose} disabled={busy} className="op-btn op-btn-ghost text-xs disabled:opacity-50" data-testid="charge-preview-cancel">
                            Annulla
                        </button>
                        <button onClick={onConfirm} disabled={busy || count === 0} className="op-btn op-btn-primary text-xs disabled:opacity-50" data-testid="charge-preview-confirm">
                            {busy ? "Eseguo…" : isWizard ? "Conferma ed esegui" : `Conferma e preleva (${formatMln(total)})`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


